import is from '@sindresorhus/is';
import type { InfiniteData } from '@tanstack/react-query';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';

import type { PaginatedJobArchitectureHistory } from '~/shared/data-contract/job-architecture-history';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { useInfiniteScrollCursor } from '../hooks/use-infinite-scroll-cursor';
import { useJobArchitectureContentLanguage } from '../providers/JobArchitectureLanguageProvider';
import { useActiveJobArchitectureId } from '../url-navigation/job-architecture-scope';
import { jobArchitectureKeys } from './query-keys';

type UseJobArchitectureHistoryReturn = {
    readonly data: InfiniteData<PaginatedJobArchitectureHistory> | undefined;
    readonly fetchNextPage: () => void;
    readonly hasNextPage: boolean;
    readonly isFetchingNextPage: boolean;
    readonly isLoading: boolean;
    readonly isRefetching: boolean;
    readonly LoadMoreCursor: ReactElement | null;
    readonly refetch: () => void;
};

export function useJobArchitectureHistory(): UseJobArchitectureHistoryReturn {
    const jobArchitectureId = useActiveJobArchitectureId();
    const fetchers = useApplicationFetchers();
    const acceptLanguage = useJobArchitectureContentLanguage();

    const infiniteQuery = useInfiniteQuery({
        enabled: is.nonEmptyString(jobArchitectureId),
        getNextPageParam: (lastPage: PaginatedJobArchitectureHistory) => {
            const { pagination } = lastPage;
            const currentPage = pagination.page;
            const totalPages = pagination.pages;

            return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
        },
        initialPageParam: 0,
        queryFn: async ({ pageParam }) => {
            const result = await fetchers.fetchJobArchitectureHistory({
                acceptLanguage,
                jobArchitectureId,
                params: {
                    paged: {
                        page: pageParam,
                        size: 20,
                    },
                },
            });

            if (result.isErr) {
                throw result.error;
            }

            return result.value;
        },
        queryKey: jobArchitectureKeys.history(jobArchitectureId),
        throwOnError: false,
    });

    const handleFetchNextPage = (): void => {
        void infiniteQuery.fetchNextPage();
    };

    const handleRefetch = (): void => {
        void infiniteQuery.refetch();
    };

    const LoadMoreCursor = useInfiniteScrollCursor({
        hasMore: infiniteQuery.hasNextPage ?? false,
        isLoading: infiniteQuery.isFetchingNextPage,
        onIntersect: handleFetchNextPage,
    });

    return {
        data: infiniteQuery.data,
        fetchNextPage: handleFetchNextPage,
        hasNextPage: infiniteQuery.hasNextPage ?? false,
        isFetchingNextPage: infiniteQuery.isFetchingNextPage,
        isLoading: infiniteQuery.isLoading,
        isRefetching: infiniteQuery.isRefetching,
        LoadMoreCursor,
        refetch: handleRefetch,
    };
}
